import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostBinding, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AnimateOnScrollDirective } from '@app/app/shared/directives/animate-on-scroll.directive';
import { FeedbackRecipient } from '@app/app/shared/models';
import { TranslocoModule } from '@ngneat/transloco';

export interface ScoreRange {
  min: number;
  max: number;
}

export interface LanguageValue {
  value?: string;
  text?: string;
  label?: string;
}

export interface ScoredLevel {
  levelUid: string;
  scoreRange: ScoreRange;
  competencyTitle: string | LanguageValue[] | undefined;
}

export interface Band {
  start: number;
  end: number;
  center: number;
  title?: string;
  levelUid?: string;
  min: number;
  max: number;
  minPercent: number;
  maxPercent: number;
  separatorPercent: number;
}

export enum LabelFormat {
  SCORE = 'SCORE',
  SCORE_PERCENT = 'SCORE_PERCENT',
  PERCENT = 'PERCENT',
}

export enum ScoredChartType {
  SCALE = 'SCALE',
  BAR = 'BAR',
}

const NAME_GAP = 8;

const RAISED_NAME_CLASS = 'is-raised';
const STAGGERED_SCALE_CLASS = 'is-staggered';

export function deriveScaleMin(levels: ScoredLevel[]): number {
  if (!levels?.length) return 0;
  const mins = levels.map((l: ScoredLevel) => Number(l?.scoreRange?.min ?? 0));
  return mins.reduce((acc: number, v: number) => Math.min(acc, v), Number.POSITIVE_INFINITY) || 0;
}

export function deriveScaleMax(levels: ScoredLevel[], maxScore?: number): number {
  const fromLevels = (levels ?? [])
    .map((l: ScoredLevel) => Number(l?.scoreRange?.max ?? NaN))
    .filter((v: number) => Number.isFinite(v) && v > 0);

  if (fromLevels.length > 0) {
    const highest = Math.max(...fromLevels);
    if (highest > 0) return highest;
  }

  if (maxScore != null && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }

  return 10;
}

export function scoreToPercent(value: number, min: number, max: number): number {
  if (!isFinite(value) || !isFinite(min) || !isFinite(max) || max === min) return 0;
  const percent = ((value - min) / (max - min)) * 100;
  return Math.max(0, Math.min(100, percent));
}

function titleFromLevel(level: ScoredLevel): string {
  const title = level?.competencyTitle;
  if (!title) return '';
  if (Array.isArray(title) && title.length) {
    const first: LanguageValue = title[0] as LanguageValue;
    return first.value ?? first.text ?? first.label ?? String(first);
  }
  return String(title);
}

export function buildScoreBands(levels: ScoredLevel[], min: number, max: number): Band[] {
  const sorted: ScoredLevel[] = (levels || []).slice().sort((a: ScoredLevel, b: ScoredLevel) => {
    const aMin = Number(a?.scoreRange?.min ?? 0);
    const bMin = Number(b?.scoreRange?.min ?? 0);
    return aMin - bMin;
  });

  if (!sorted.length) {
    return [
      {
        start: 0,
        end: 100,
        center: 50,
        title: undefined,
        min,
        max,
        minPercent: 0,
        maxPercent: 100,
        separatorPercent: 100,
      },
    ];
  }

  const actualPositions = sorted.map((lvl) => {
    const lmin = Number(lvl?.scoreRange?.min ?? min);
    const lmax = Number(lvl?.scoreRange?.max ?? lmin);
    return {
      min: lmin,
      max: lmax,
      minPercent: scoreToPercent(lmin, min, max),
      maxPercent: scoreToPercent(lmax, min, max),
    };
  });

  const bands: Band[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const lvl = sorted[i];
    const { min: lmin, max: lmax, minPercent: actualStart, maxPercent: actualEnd } = actualPositions[i];
    const title = titleFromLevel(lvl);

    let start: number;
    let end: number;
    let separatorPercent: number;

    if (i === 0) {
      start = actualStart;
    } else {
      const prevEnd = actualPositions[i - 1].maxPercent;
      start = (prevEnd + actualStart) / 2;
    }

    if (i === sorted.length - 1) {
      end = actualEnd;
      separatorPercent = actualEnd;
    } else {
      const nextStart = actualPositions[i + 1].minPercent;
      end = (actualEnd + nextStart) / 2;
      separatorPercent = (actualEnd + nextStart) / 2;
    }

    const next = actualPositions[i + 1];
    const nextPointHasSameNumber = next != null && next.min === lmin && next.max === lmax;

    const center = (start + end) / 2;
    bands.push({
      start,
      end,
      center,
      title: nextPointHasSameNumber ? undefined : title || undefined,
      levelUid: lvl?.levelUid,
      min: lmin,
      max: lmax,
      minPercent: actualStart,
      maxPercent: actualEnd,
      separatorPercent,
    });
  }
  return bands;
}

@Component({
  selector: 'ptl-competency-scale',
  standalone: true,
  imports: [CommonModule, TranslocoModule, AnimateOnScrollDirective],
  templateUrl: './competency-scale.component.html',
  styleUrls: ['./competency-scale.component.scss'],
})
export class CompetencyScaleComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() levels: ScoredLevel[] = [];
  @Input() maxScore?: number;
  @Input() userScore?: number;
  @Input() avgRatingScore?: number;
  @Input() isMockView = false;
  @Input() labelFormat: LabelFormat = LabelFormat.SCORE_PERCENT;
  @Input() chartType: ScoredChartType = ScoredChartType.SCALE;
  @Input() feedbackRecipient?: FeedbackRecipient;

  /**
   * Draw each band name on one line, lifting the ones that would collide onto a second row, instead
   * of wrapping a name into the width of its own band. v3 opts in; v2 keeps the wrapped names, whose
   * layout is settled at any width and so survives being printed.
   */
  @Input() singleLineBandNames = false;

  selfScore?: number;
  avgScore?: number;

  bands: Band[] = [];
  ticks: number[] = [];
  selfPercent = 0;
  avgPercent = 0;
  selfPillLabel = '-';
  avgPillLabel = '-';
  selfMarkerLabel = '-';
  avgMarkerLabel = '-';

  effectiveMin = 0;
  effectiveMax = 10;
  tickValues: number[] = [];
  selfVisible = false;
  avgVisible = false;

  protected readonly ScoredChartType = ScoredChartType;
  private readonly TOTAL_TICKS = 11;

  private readonly host = inject(ElementRef<HTMLElement>);
  private resizeObserver?: ResizeObserver;

  @HostBinding('class.has-single-line-names')
  get hasSingleLineNames(): boolean {
    return this.singleLineBandNames;
  }

  ngOnChanges(_: SimpleChanges): void {
    const min = deriveScaleMin(this.levels);
    const max = deriveScaleMax(this.levels, this.maxScore);
    const effectiveMax = Math.max(min, max);

    this.effectiveMin = min;
    this.effectiveMax = effectiveMax;

    if (this.userScore) {
      this.selfScore = this.userScore;
    } else if (this.isMockView) {
      this.selfScore = Math.round((min + effectiveMax) / 2);
    } else {
      this.selfScore = 0;
    }
    if (this.avgRatingScore) {
      this.avgScore = this.avgRatingScore;
    } else if (this.isMockView) {
      this.avgScore = Math.round((min + effectiveMax) / 2 + 1);
    } else {
      this.avgScore = 0;
    }

    this.bands = buildScoreBands(this.levels, min, max);
    this.buildTicksFixed(min, max);
    this.buildMarkers(min, max);
    if (this.singleLineBandNames) {
      // The new bands are laid out once this change has been rendered.
      setTimeout(() => this.stackBandNames());
    }
  }

  ngAfterViewInit(): void {
    if (!this.singleLineBandNames) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.stackBandNames());
    this.resizeObserver.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private stackBandNames(): void {
    const host: HTMLElement = this.host.nativeElement;
    const names = Array.from(host.querySelectorAll<HTMLElement>('.band-title'));
    names.forEach((name) => name.classList.remove(RAISED_NAME_CLASS));

    let firstRowEnd = Number.NEGATIVE_INFINITY;
    let anyRaised = false;

    for (const name of names) {
      const box = name.getBoundingClientRect();
      if (box.left >= firstRowEnd + NAME_GAP) {
        firstRowEnd = box.right;
      } else {
        name.classList.add(RAISED_NAME_CLASS);
        anyRaised = true;
      }
    }

    host.classList.toggle(STAGGERED_SCALE_CLASS, anyRaised);
  }

  private round3(v: number): number {
    return Math.round(v * 1000) / 1000;
  }

  private round1(v: number): number {
    return Math.round(v * 10) / 10;
  }

  private buildTicksFixed(min: number, max: number): void {
    const ticks: number[] = [];
    const step = 100 / (this.TOTAL_TICKS - 1);
    for (let i = 0; i < this.TOTAL_TICKS; i++) {
      ticks.push(this.round3(Math.max(0, Math.min(100, i * step))));
    }
    this.ticks = ticks;

    const range = Math.max(0, max - min);
    this.tickValues = ticks.map((pct) => {
      const val = min + (pct / 100) * range;
      return Math.round(val);
    });
  }

  private fmtScore(v?: number) {
    return v == null ? '-' : `${v}`;
  }
  private fmtPercent(p?: number) {
    return p == null ? '-' : `${this.round1(p)}%`;
  }

  private buildMarkers(min: number, max: number): void {
    this.selfPercent = this.round1(scoreToPercent(Number(this.selfScore ?? min), min, max));
    this.avgPercent = this.round1(scoreToPercent(Number(this.avgScore ?? min), min, max));

    switch (this.labelFormat) {
      case LabelFormat.PERCENT:
        this.selfPillLabel = this.fmtPercent(this.selfPercent);
        this.avgPillLabel = this.fmtPercent(this.avgPercent);
        this.selfMarkerLabel = this.fmtPercent(this.selfPercent);
        this.avgMarkerLabel = this.fmtPercent(this.avgPercent);
        break;
      case LabelFormat.SCORE_PERCENT:
        this.selfPillLabel = this.fmtPercent(this.selfPercent);
        this.avgPillLabel = this.fmtPercent(this.avgPercent);
        this.selfMarkerLabel = this.fmtScore(this.selfScore);
        this.avgMarkerLabel = this.fmtScore(this.avgScore);
        break;
      case LabelFormat.SCORE:
      default:
        this.selfPillLabel = this.fmtScore(this.selfScore);
        this.avgPillLabel = this.fmtScore(this.avgScore);
        this.selfMarkerLabel = this.fmtScore(this.selfScore);
        this.avgMarkerLabel = this.fmtScore(this.avgScore);
    }
  }

  protected readonly LabelFormat = LabelFormat;
}
